/**
 * src/hooks/useDictationSafetyNet.js — TICKET-936.
 *
 * Composer side of the text safety store. `missing` = saved dictations that are
 * no longer in the box (a button cleared or replaced it, the chat was closed
 * mid-transcription, the mode chip swapped the input). The composer offers them
 * back; they leave the store only on send or an explicit discard.
 *
 * Every present -> missing transition emits `dictation_text_dropped` once, so
 * the next "I lost my dictation" report comes with the path that did it.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import {
  listSafeDictations, forgetDictation, isPresent, SAFETY_EVENT,
} from '../lib/voice/dictationSafety.js';
import { logVoiceEvent } from '../lib/voice/voiceLogger.js';

const emit = (step, message, meta) => {
  try { logVoiceEvent(step, step === 'dictation_text_dropped' ? 'error' : 'ok', message, { source: 'client', module: 'voice', ...meta }); }
  catch { /* telemetry must never break the composer */ }
};

export default function useDictationSafetyNet({ input, scope = 'chat' }) {
  const [entries, setEntries] = useState(() => listSafeDictations(scope));
  const seen = useRef(new Set()); // ids that were in the box at some point
  const reported = useRef(new Set());

  useEffect(() => {
    const refresh = () => setEntries(listSafeDictations(scope));
    refresh();
    window.addEventListener(SAFETY_EVENT, refresh);
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener(SAFETY_EVENT, refresh);
      window.removeEventListener('storage', refresh);
    };
  }, [scope]);

  const missing = entries.filter((e) => !isPresent(e.text, input));

  useEffect(() => {
    for (const e of entries) {
      if (isPresent(e.text, input)) { seen.current.add(e.id); continue; }
      if (seen.current.has(e.id) && !reported.current.has(e.id)) {
        reported.current.add(e.id);
        emit('dictation_text_dropped', 'dictated text left the composer without being sent — offered back',
          { scope_id: e.id, chars: e.text.length, reason: scope });
      }
    }
  }, [entries, input, scope]);

  /** On send: every saved dictation that is in the sent text is done. */
  const settleSent = useCallback((sent) => {
    for (const e of listSafeDictations(scope)) if (isPresent(e.text, sent)) forgetDictation(e.id);
  }, [scope]);

  const restored = useCallback((id) => {
    reported.current.delete(id);
    emit('dictation_text_restored', 'user restored a dictation to the composer', { scope_id: id, reason: scope });
  }, [scope]);

  const discard = useCallback((id) => {
    emit('dictation_text_discarded', 'user discarded a saved dictation text', { scope_id: id, reason: scope });
    forgetDictation(id);
  }, [scope]);

  return { missing, settleSent, restored, discard };
}
